import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Divider,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import React, { useState } from 'react'
import { CommentReport, PostReport, ProfileReport } from './FlaggedContent';
import defaultAvatars from "../../../../../assets/defaultAvatars.svg";
import { Link } from "react-router-dom";

export const useStyles = makeStyles(theme => ({
  contentCard: {
    backgroundColor: "#1a1a1a",
    border: "1px solid #333",
    marginBottom: theme.spacing(2),
    color: "#fff",
    "&:hover": {
      borderColor: "#FE5A97",
    },
  },
  contentHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  contentPreview: {
    maxWidth: "200px",
    maxHeight: "200px",
    borderRadius: "8px",
    objectFit: "cover",
  },
  contentPreviewContainer: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: "#2a2a2a",
    borderRadius: "8px",
    border: "1px solid #444",
  },
  contentPreviewImage: {
    maxWidth: "100%",
    maxHeight: "400px",
    borderRadius: "8px",
    objectFit: "contain",
    marginBottom: theme.spacing(1),
  },
  contentPreviewVideo: {
    maxWidth: "100%",
    maxHeight: "400px",
    borderRadius: "8px",
    marginBottom: theme.spacing(1),
  },
  contentPreviewText: {
    padding: theme.spacing(1.5),
    backgroundColor: "#1a1a1a",
    borderRadius: "4px",
    border: "1px solid #333",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  postStatusBadge: {
    marginLeft: theme.spacing(1),
  },
  expandButton: {
    marginTop: theme.spacing(1),
    color: "#FE5A97",
    textTransform: "none",
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
  },
  approveButton: {
    backgroundColor: "#4caf50",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#45a049",
    },
  },
  rejectButton: {
    backgroundColor: "#f44336",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#da190b",
    },
  },
}));

type ReportCardProps = {
  report: PostReport,
  reportType: "post" | "profile" | "comment" | null,
  loadingAvatars: {
    [key: string]: boolean;
  },
  avatarUrls: { [key: string]: string },
  mediaUrls: { [key: string]: string },
  loadingMedia: {
    [key: string]: boolean
  },
  fetchMediaUrl: (mediaUrl: string, key: string) => void,
  setReportStatusDialog: React.Dispatch<React.SetStateAction<{
    open: boolean;
    report: PostReport | ProfileReport | CommentReport | null;
    status: string;
    moderatorComment?: string;
    reportType?: "post" | "profile" | "comment" | null;
  }>>
}

export default function ReportCard({ report, reportType, loadingAvatars, avatarUrls, mediaUrls, loadingMedia, fetchMediaUrl, setReportStatusDialog }: ReportCardProps) {
  const classes = useStyles();
  const [expandedPostReports, setExpandedPostReports] = useState<{
    [key: string]: boolean;
  }>({});

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Card key={report._id} className={classes.contentCard}>
      <CardContent>
        <Box className={classes.contentHeader}>
          <Box className={classes.userInfo}>
            {loadingAvatars[report.postOwner?._id || ""] ? (
              <Avatar>
                <CircularProgress size={24} />
              </Avatar>
            ) : (
              <Avatar
                src={
                  report.postOwner?._id && avatarUrls[report.postOwner._id]
                    ? avatarUrls[report.postOwner._id]
                    : report.postOwner?.profileImg
                      ? report.postOwner.profileImg
                      : defaultAvatars
                }
                alt={report.postOwner?.username || "User"}
              >
                {!report.postOwner?.profileImg &&
                  !avatarUrls[report.postOwner?._id || ""] &&
                  (report.postOwner?.firstName?.[0] ||
                    report.postOwner?.username?.[0] ||
                    "U")}
              </Avatar>
            )}
            <Box>
              <Typography variant="h6" style={{ color: "#fff" }}>
                {report.postOwner?.username ? (
                  <Link
                    to={`/${report.postOwner.username}`}
                    style={{
                      color: "#FE5A97",
                      textDecoration: "none",
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.textDecoration = "underline";
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.textDecoration = "none";
                    }}
                  >
                    {report.postOwner.username}
                  </Link>
                ) : (
                  report.postOwner?.firstName || "Unknown User"
                )}
              </Typography>
              <Typography variant="body2" style={{ color: "#ccc" }}>
                {report.postOwner?.email || "No email available"}
              </Typography>
            </Box>
          </Box>
          {report.reporter && (
            <Box
              className={classes.userInfo}
              style={{ marginTop: "12px", marginBottom: "8px" }}
            >
              {loadingAvatars[report.reporter._id] ? (
                <Avatar>
                  <CircularProgress size={24} />
                </Avatar>
              ) : (
                <Avatar
                  src={
                    report.reporter._id && avatarUrls[report.reporter._id]
                      ? avatarUrls[report.reporter._id]
                      : report.reporter.profileImg
                        ? report.reporter.profileImg
                        : defaultAvatars
                  }
                  alt={report.reporter.username || "Reporter"}
                  style={{ width: 32, height: 32 }}
                >
                  {!report.reporter.profileImg &&
                    !avatarUrls[report.reporter._id] &&
                    (report.reporter.firstName?.[0] ||
                      report.reporter.username?.[0] ||
                      "R")}
                </Avatar>
              )}
              <Box>
                <Typography
                  variant="body2"
                  style={{ color: "#999", fontSize: "12px" }}
                >
                  Reported by:
                </Typography>
                <Typography variant="body2" style={{ color: "#fff" }}>
                  {report.reporter.username ? (
                    <Link
                      to={`/${report.reporter.username}`}
                      style={{
                        color: "#FE5A97",
                        textDecoration: "none",
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.textDecoration = "underline";
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.textDecoration = "none";
                      }}
                    >
                      {report.reporter.username}
                    </Link>
                  ) : (
                    report.reporter.firstName || "Unknown"
                  )}
                </Typography>
              </Box>
            </Box>
          )}
          <Box display="flex" alignItems="center" style={{ gap: "8px" }}>
            <Chip
              label={
                report.status === "postDeleted"
                  ? "POST BANNED"
                  : report.status.toUpperCase()
              }
              variant="outlined"
              size="small"
              style={{
                color: "#fff",
                borderColor:
                  report.status === "open"
                    ? "#4caf50"
                    : report.status === "pending"
                      ? "#ff9800"
                      : report.status === "reviewing"
                        ? "#ff9800"
                        : report.status === "resolved"
                          ? "#2196f3"
                          : report.status === "postDeleted"
                            ? "#f44336"
                            : "#999",
                backgroundColor:
                  report.status === "postDeleted"
                    ? "rgba(244, 67, 54, 0.1)"
                    : "transparent",
              }}
            />
            {report.post?.status && (
              <Chip
                label={`Post: ${report.post.status === "postDeleted"
                  ? "BANNED / DELETED"
                  : report.post.status.toUpperCase()
                  }`}
                variant="outlined"
                size="small"
                className={classes.postStatusBadge}
                style={{
                  color: "#fff",
                  borderColor:
                    report.post.status === "postDeleted"
                      ? "#f44336"
                      : "#FE5A97",
                  backgroundColor:
                    report.post.status === "postDeleted"
                      ? "rgba(244, 67, 54, 0.1)"
                      : "transparent",
                }}
              />
            )}
          </Box>
        </Box>

        <Divider style={{ backgroundColor: "#333", margin: "16px 0" }} />

        <Box>
          <Typography
            variant="body1"
            style={{ marginBottom: "8px", color: "#fff" }}
          >
            <strong>Post ID:</strong>
            <Link
              to={`/?postId=${report.postId}`}
              style={{
                color: "#FE5A97",
                textDecoration: "none",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.textDecoration =
                  "underline";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.textDecoration = "none";
              }}
            >
              {report.postId}
            </Link>

          </Typography>
          <Typography
            variant="body1"
            style={{ marginBottom: "8px", color: "#fff" }}
          >
            <strong>Reason:</strong> {report.reason}
          </Typography>
          {report.comment && (
            <Typography
              variant="body2"
              style={{ marginBottom: "8px", color: "#fff" }}
            >
              <strong>Comment:</strong> {report.comment}
            </Typography>
          )}

          {(report.aiFlags && report.aiFlags.length > 0) && (
            <Typography
              variant="body2"
              style={{ marginBottom: "8px", color: "#fff" }}
            >
              <strong>AI Flags:</strong>
              <ul>
                {report.aiFlags.map(flag => (
                  <li key={flag._id}>
                    <strong>Field:</strong> {flag.field}<br />
                    <strong>Severity:</strong> {flag.severity}<br />
                    <strong>Provider:</strong> {flag.provider}<br />
                    <strong>Original Text:</strong> {flag.originalText}<br />
                    <strong>Matched Terms:</strong> {flag.matchedTerms.join(", ")}
                  </li>
                ))}
              </ul>
            </Typography>
          )}

          {/* Expandable Post Content */}
          <Box style={{ marginTop: "16px" }}>
            <Button
              className={classes.expandButton}
              onClick={() =>
                setExpandedPostReports(prev => ({
                  ...prev,
                  [report._id]: !prev[report._id],
                }))
              }
              size="small"
            >
              {expandedPostReports[report._id] ? "Hide" : "Show"} Post Details
            </Button>
            {expandedPostReports[report._id] && (
              <Box className={classes.contentPreviewContainer}>
                {report.post?.description && (
                  <Typography
                    variant="body2"
                    className={classes.contentPreviewText}
                    style={{ color: "#fff", marginBottom: "16px" }}
                  >
                    <strong>Post Description:</strong> {report.post.description}
                  </Typography>
                )}
                {report.post?.content && report.post.content.length > 0 && (
                  <Box>
                    <Typography
                      variant="body2"
                      style={{
                        color: "#FE5A97",
                        marginBottom: "8px",
                      }}
                    >
                      <strong>Post Content:</strong>
                    </Typography>
                    <Box display="flex" flexWrap="wrap" style={{ gap: "8px" }}>
                      {report.post.content.map((item, index) => {
                        const mediaKey = `post_${report.postId}_${index}`;
                        if (
                          item.mediaUrl &&
                          item.mediaUrl.startsWith("/api/files/")
                        ) {
                          if (!mediaUrls[mediaKey] && !loadingMedia[mediaKey]) {
                            fetchMediaUrl(item.mediaUrl, mediaKey);
                          }
                        }
                        const displayUrl = mediaUrls[mediaKey] || item.mediaUrl;

                        return (
                          <Box key={index} style={{ marginBottom: "8px" }}>
                            {item.type === "image" && (
                              <Box>
                                {loadingMedia[mediaKey] ? (
                                  <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    style={{
                                      minHeight: "200px",
                                      width: "200px",
                                      backgroundColor: "#1a1a1a",
                                    }}
                                  >
                                    <CircularProgress size={24} />
                                  </Box>
                                ) : (
                                  <img
                                    src={displayUrl}
                                    alt={`Post content ${index + 1}`}
                                    className={classes.contentPreviewImage}
                                    style={{
                                      maxWidth: "300px",
                                      maxHeight: "300px",
                                    }}
                                    onError={e => {
                                      (e.target as HTMLImageElement).src =
                                        defaultAvatars;
                                    }}
                                  />
                                )}
                                {item.paidContent && (
                                  <Chip
                                    label="Paid"
                                    size="small"
                                    style={{
                                      backgroundColor: "#FE5A97",
                                      color: "#fff",
                                      marginTop: "4px",
                                    }}
                                  />
                                )}
                              </Box>
                            )}
                            {item.type === "video" && (
                              <Box>
                                {loadingMedia[mediaKey] ? (
                                  <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    style={{
                                      minHeight: "200px",
                                      width: "200px",
                                      backgroundColor: "#1a1a1a",
                                    }}
                                  >
                                    <CircularProgress size={24} />
                                  </Box>
                                ) : (
                                  <video
                                    src={displayUrl}
                                    controls
                                    className={classes.contentPreviewVideo}
                                    style={{
                                      maxWidth: "300px",
                                      maxHeight: "300px",
                                    }}
                                  >
                                    Your browser does not support the video tag.
                                  </video>
                                )}
                                {item.paidContent && (
                                  <Chip
                                    label="Paid"
                                    size="small"
                                    style={{
                                      backgroundColor: "#FE5A97",
                                      color: "#fff",
                                      marginTop: "4px",
                                    }}
                                  />
                                )}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
                {report.post?.hashtags && report.post.hashtags.length > 0 && (
                  <Box style={{ marginTop: "16px" }}>
                    <Typography
                      variant="body2"
                      style={{ color: "#fff", marginBottom: "4px" }}
                    >
                      <strong>Hashtags:</strong>
                    </Typography>
                    <Box display="flex" flexWrap="wrap" style={{ gap: "4px" }}>
                      {report.post.hashtags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={`#${tag}`}
                          size="small"
                          style={{
                            backgroundColor: "#333",
                            color: "#FE5A97",
                            fontSize: "12px",
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
                {report.post?.price && (
                  <Typography
                    variant="body2"
                    style={{ color: "#FE5A97", marginTop: "16px" }}
                  >
                    <strong>Price:</strong> ${report.post.price}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          <Typography variant="body2" style={{ color: "#ccc" }}>
            <strong>Reported:</strong> {formatDate(report.createdAt)}
          </Typography>
        </Box>

        <Box
          className={classes.actionButtons}
          style={{ marginTop: "16px" }}
        >
          <Button
            variant="contained"
            className={classes.approveButton}
            onClick={() =>
              setReportStatusDialog({
                open: true,
                report,
                status: report.status || "open",
                moderatorComment: "",
                reportType
              })
            }
          >
            Change Status
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
